import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import AccordionWrapper from 'App/components/AccordionWrapper/AccordionWrapper';
import ManualPageTable from 'App/components/ManualPageTable';
import { hasLength } from 'App/utils';
import styled from '@emotion/styled';
import { useFormikContext } from 'formik';
import { processWorkQueueResult, updateWorkQueueFilters } from 'App/features/WorkQueue/workQueue.utils';
import { defaultActivityDateModalState, defaultActivityNoteModalState } from '../../defaultWorkQueueState';
import EditActivityDueDateModal from 'App/components/PatientProfile/components/PatientActivities/components/EditActivityDueDateModal';
import ActivityNotesModal from 'App/components/PatientProfile/components/PatientActivities/components/ActivityNotesModal';
import { queryClient } from 'Shared/queryClient';
import { useGetWorkQueueColumns } from '../../hooks/useGetWorkQueueColumns';
import { useGetHeaderMappingForMobileAnswersQueue } from '../../hooks/useGetHeaderMappingForMobileAnswersQueue';
import { useUserContext } from '@/app/contexts/UserContext';
import useMobileAnswersQueue from '../../hooks/useMobileAnswersQueue';
import * as R from 'ramda';

const MarginDiv = styled('div')`
    margin-bottom: 30px;
`;

const AssignmentMarginDiv = styled('div')`
    margin-top: 10px;
    margin-bottom: 10px;
`;

const propTypes = {
    isAssignmentManager: PropTypes.bool,
};

const defaultProps = {
    isAssignmentManager: false,
};

const MobileCompletedActivitiesTable = ({ isAssignmentManager }) => {
    const {
        values: {
            modalMobileAnswersState,
            filterParams,
            mobileAnswersFilterParams,
            allBranchesList,
            allCategoriesList,
            allActivityTypesList,
            allPatientLabelsList,
            allCustomOptionsList,
        },
        setFieldValue,
    } = useFormikContext();
    const headerMapping = useGetHeaderMappingForMobileAnswersQueue({ isAssignmentManager });
    const user = useUserContext();
    const { page, count, search, sorting } = mobileAnswersFilterParams;
    const [searchState, setSearchState] = useState(search || {});
    const [lazyFilter, setLazyFilter] = useState(() =>
        updateWorkQueueFilters(
            {
                ...R.omit(['dateStart', 'dateEnd', 'type'], filterParams),
                ...mobileAnswersFilterParams,
            },
            allBranchesList,
            allCategoriesList,
            allActivityTypesList,
            allPatientLabelsList,
            allCustomOptionsList
        )
    );
    const { data: mobileAnswersComplete, isLoading: isSubmitting } = useMobileAnswersQueue(lazyFilter);
    const processedData = useMemo(
        () => (mobileAnswersComplete?.data ? processWorkQueueResult(mobileAnswersComplete.data) : []),
        [mobileAnswersComplete]
    );
    const onFilterChange = useCallback(
        (e) => {
            const prevSearch = { ...searchState };
            if (e.target.value === '') {
                delete prevSearch[e.target.name];
            } else {
                prevSearch[e.target.name] = e.target.value;
            }
            setSearchState(prevSearch);
        },
        [searchState]
    );

    useEffect(() => {
        const timer = setTimeout(() => {
            setFieldValue('mobileAnswersFilterParams.search', searchState);
        }, 800);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchState]);

    useEffect(() => {
        if (!R.equals(searchState, search)) {
            setSearchState(search);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    useEffect(() => {
        const params = R.omit(['dateStart', 'dateEnd', 'type'], filterParams);
        const filterMobileParams = {
            ...params,
            ...mobileAnswersFilterParams,
        };
        const normalizedFilters = updateWorkQueueFilters(
            filterMobileParams,
            allBranchesList,
            allCategoriesList,
            allActivityTypesList,
            allPatientLabelsList,
            allCustomOptionsList
        );
        setLazyFilter(normalizedFilters);
    }, [
        filterParams,
        mobileAnswersFilterParams,
        allBranchesList,
        allCategoriesList,
        allActivityTypesList,
        allPatientLabelsList,
        allCustomOptionsList,
    ]);

    const refetchWorkQueueMobileAnswersResult = () => {
        queryClient.refetchQueries([
            'company',
            user.active_company.ID,
            'branch',
            user.active_branch.ID,
            'workQueue',
            'mobileAnswersComplete',
            JSON.stringify(lazyFilter),
        ]);
    };
    const onHideActivityDueDateModal = (refetch) => {
        if (refetch) {
            refetchWorkQueueMobileAnswersResult();
        }
        setFieldValue('modalMobileAnswersState.mobileAnswersActivityDateModal', defaultActivityDateModalState);
    };

    const onHideActivityNoteStatus = (refetch) => {
        if (refetch) {
            refetchWorkQueueMobileAnswersResult();
        }
        setFieldValue('modalMobileAnswersState.mobileAnswersActivityNoteModal', defaultActivityNoteModalState);
    };

    const columns = useGetWorkQueueColumns({ headerMapping, onFilterChange, searchState, isAssignmentManager });

    const canSort = hasLength(processedData);

    const onPageChange = ({ pageIndex, pageSize }) => {
        setFieldValue('mobileAnswersFilterParams.page', pageIndex);
        setFieldValue('mobileAnswersFilterParams.count', pageSize);
    };

    const onSortChange = (sort) => {
        const sortField = sort.sortKey;
        setFieldValue('mobileAnswersFilterParams.sorting', {
            [sortField]: sort.sortOrder,
        });
    };

    const sortKey = Object.keys(sorting || {})[0] ?? 'patient_name';
    const sortOrder = sorting ? sorting[sortKey] : 'asc';

    const mobileAnswersActivityDateModalState = modalMobileAnswersState?.mobileAnswersActivityDateModal;
    const mobileAnswersActivityNoteModalState = modalMobileAnswersState?.mobileAnswersActivityNoteModal;

    const Wrapper = isAssignmentManager ? AssignmentMarginDiv : MarginDiv;
    const stateKey = isAssignmentManager
        ? 'assignmentMobileAnswersCompleteActivities-toggle'
        : 'mobileAnswersCompleteActivities-toggle';

    return (
        <AccordionWrapper
            title={`Mobile Completed Activities (${mobileAnswersComplete?.count ?? 0})`}
            stateKey={stateKey}
            defaultIsVisible={false}
        >
            <Wrapper>
                <ManualPageTable
                    data={processedData}
                    totalRows={mobileAnswersComplete?.count}
                    placeholderText="There are no activities that match your current search criteria."
                    columns={columns}
                    canSort={canSort}
                    isLoading={isSubmitting}
                    onPageChange={onPageChange}
                    onSortChange={onSortChange}
                    pageSizeOptions={[10, 25, 50]}
                    currentPageIndex={count}
                    currentPage={page}
                    initialSortKey={sortKey}
                    initialSortOrder={sortOrder}
                />
                {!isAssignmentManager && mobileAnswersActivityDateModalState?.show && (
                    <EditActivityDueDateModal
                        onHide={onHideActivityDueDateModal}
                        {...mobileAnswersActivityDateModalState}
                    />
                )}
                {!isAssignmentManager && mobileAnswersActivityNoteModalState?.show && (
                    <ActivityNotesModal onHide={onHideActivityNoteStatus} {...mobileAnswersActivityNoteModalState} />
                )}
            </Wrapper>
        </AccordionWrapper>
    );
};

MobileCompletedActivitiesTable.propTypes = propTypes;
MobileCompletedActivitiesTable.defaultProps = defaultProps;

export default MobileCompletedActivitiesTable;
